import { z } from "zod";
import { NameUse } from "@/graphql/generated/types";

// The open Studio notebook as it rides every save/studio turn (forwardedProps.studioNotebook,
// @external-adr AGENT-SERVICE-0036-the-studio-proposes-records-the-user-decides). Mirrors the BFF caps.
export interface StudioNotebookContext {
  name: string;
  notes: { id: string; text: string }[];
}

// The model writes `null` for a field it leaves out as often as it omits the key: both mean "not given",
// and neither may cost the whole entry.
const optionalText = z
  .string()
  .trim()
  .nullish()
  .transform((value) => (value ? value : undefined))
  .optional();

const optionalInt = z
  .number()
  .int()
  .nullish()
  .transform((value) => value ?? undefined)
  .optional();

// The prompt shows a source as "[note s1]", "[answer a1]" or "[document d1]", and a model cites it that way now and then
// ("answer a1", seen live 2026-09-28): the id is what counts, not the word before it or a bracket around it.
const citedId = (id: string) =>
  id.trim().replace(/^[[`\s]*(?:(?:note|notiz|answer|antwort|document|dokument)\s+)?/i, "").replace(/[\]`\s]+$/, "");

// No ids at all (a model proposing from a web answer) is not a broken entry but one without a Beleg: it is
// turned down with a reason on the card, rather than dropped so silently that no card appears.
const sourceNoteIds = z
  .array(z.string().min(1))
  .nullish()
  .transform((value) => (value ?? []).map(citedId));

const personSchema = z.object({
  givenName: optionalText,
  familyName: optionalText,
  email: optionalText,
  jobTitle: optionalText,
  currentEmployer: optionalText,
  city: optionalText,
  website: optionalText,
  phone: optionalText,
  experiences: z.array(z.unknown()).nullish(),
  educations: z.array(z.unknown()).nullish(),
  expertises: z.array(z.unknown()).nullish(),
  sourceNoteIds,
});

const expertiseSchema = z.object({ description: optionalText });

const period = { startYear: optionalInt, startMonth: optionalInt, endYear: optionalInt, endMonth: optionalInt };

const experienceSchema = z.object({ jobTitle: optionalText, employer: optionalText, location: optionalText, ...period });

const educationSchema = z.object({
  field: optionalText,
  degree: optionalText,
  institution: optionalText,
  city: optionalText,
  ...period,
});

const companySchema = z.object({
  name: z.string().trim().min(1),
  employeeCount: optionalInt.refine((value) => value === undefined || value >= 0),
  summary: optionalText,
  expertises: z.array(z.unknown()).nullish(),
  sourceNoteIds,
});

// A value the model proposed that none of the entry's cited notes contains, so it was left out.
export interface DroppedField {
  field: string;
  value: string;
}

export interface YearMonth {
  year: number;
  month?: number;
}

// "2001-07" or "2001", as MonthYearSelect and genesis's date input read it; "" when there is no date.
export const toMonthYear = (value?: YearMonth) =>
  value ? (value.month ? `${value.year}-${String(value.month).padStart(2, "0")}` : String(value.year)) : "";

// A position the notes name for a person. genesis needs a job title and a start to create one and reads a
// missing end as "current", so a position without a stated title or start, or without an end the notes do not
// call current, carries the problem and is shown as not taken over.
export interface StudioExperienceProposal {
  jobTitle?: string;
  employer?: string;
  location?: string;
  start?: YearMonth;
  end?: YearMonth;
  problem?: "noTitle" | "noStart" | "noEnd";
}

// A degree or course of study the notes name for a person. genesis needs it to name something: a subject
// (`field`), an institution or a degree; subject and start are optional. The degree is kept as the notes write it
// and matched to genesis's degree catalogue only when applying.
export interface StudioEducationProposal {
  field?: string;
  degree?: string;
  institution?: string;
  city?: string;
  start?: YearMonth;
  end?: YearMonth;
  problem?: "noContent" | "noEnd";
}

// A subject, method or topic the notes or answers name for a person ("E=mc²", "Relativitätstheorie"): genesis
// keeps it as an expertise's description, in the source's own words.
export interface StudioExpertiseProposal {
  description: string;
}

// What "Create" made of an entry. Kept with the card in the notebook, so an entry is created once and its record
// stays one click away after a reload.
export interface StudioCreatedRecord {
  id: string;
}

// The entry in the organizational record that says where a created record came from (spark #311, #947). Kept on
// the entry, so the note is written once and a reload still knows it is there.
export interface StudioProvenance {
  entryId: string;
}

export type StudioPersonProposal = Omit<z.infer<typeof personSchema>, "experiences" | "educations" | "expertises"> & {
  experiences: StudioExperienceProposal[];
  educations: StudioEducationProposal[];
  // Absent on a card kept before 2026-09-28.
  expertises?: StudioExpertiseProposal[];
  dropped: DroppedField[];
  // The id the person is created under, fixed before the first write: a second attempt after a reload reuses
  // it, and genesis refuses an id it already has rather than creating the person twice.
  personId?: string;
  created?: StudioCreatedRecord;
  provenance?: StudioProvenance;
};
// A company's expertise is the same thing as a person's (genesis stores it as a company competency, CONTEXT.md
// "Company expertise"): a label in the notes' own words.
export type StudioCompanyProposal = Omit<z.infer<typeof companySchema>, "expertises"> & {
  // Absent on a card kept before 2026-10-02.
  expertises?: StudioExpertiseProposal[];
  dropped: DroppedField[];
  created?: StudioCreatedRecord;
  provenance?: StudioProvenance;
};

const PERSON_FIELDS = ["email", "jobTitle", "currentEmployer", "city", "website", "phone"] as const;

// Case, umlaut spelling ("für"/"fuer"), accents and spacing do not count as a difference.
export function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function mentions(value: string, source: string): boolean {
  return normalize(source).includes(normalize(value));
}

// "(Einkauf?)" or "12 (?)" is the user's guess, not a fact: a value counts only where a note gives it
// without a question mark right after it.
const GUESS_MARK = String.raw`[\s()[\]]*\?`;
const TENTATIVE = new RegExp(`^${GUESS_MARK}`);

function states(value: string, source: string): boolean {
  const text = normalize(source);
  const needle = normalize(value);
  for (let at = text.indexOf(needle); at !== -1; at = text.indexOf(needle, at + 1)) {
    if (!TENTATIVE.test(text.slice(at + needle.length))) return true;
  }
  return false;
}

// A count as the notes write it, with or without thousands separators: "31000", "31.000", "31 000", "31,000".
function statesCount(count: number, source: string): boolean {
  const groups = String(count).replace(/\B(?=(\d{3})+(?!\d))/g, " ").split(" ");
  return new RegExp(`\\b${groups.join("[.,\\s\\u00a0\\u202f]?")}\\b(?!${GUESS_MARK})`).test(source);
}

const MONTH_NAMES = [
  ["januar", "january", "jan"],
  ["februar", "february", "feb"],
  ["maerz", "march", "mrz", "mar"],
  ["april", "apr"],
  ["mai", "may"],
  ["juni", "june", "jun"],
  ["juli", "july", "jul"],
  ["august", "aug"],
  ["september", "sept", "sep"],
  ["oktober", "october", "okt", "oct"],
  ["november", "nov"],
  ["dezember", "december", "dez", "dec"],
] as const;

// A month counts only right before its year: "Juli 2001", "Jul. 2001", "07/2001", "7.2001" or "2001-07".
function statesMonth(month: number, year: number, source: string): boolean {
  const names = MONTH_NAMES[month - 1];
  if (!names) return false;
  const mm = String(month).padStart(2, "0");
  return new RegExp(`\\b(?:(?:${names.join("|")})\\.?\\s*|0?${month}\\s*[./]\\s*)${year}\\b|\\b${year}-${mm}\\b`).test(
    normalize(source),
  );
}

function statedYearMonth(year: number | undefined, month: number | undefined, source: string): YearMonth | undefined {
  if (year === undefined || !statesCount(year, source)) return undefined;
  return month !== undefined && statesMonth(month, year, source) ? { year, month } : { year };
}

const CURRENT = /\b(?:seit|aktuell|derzeit|momentan|zurzeit|heute|jetzt|since|current|currently|present|now|today)\b/;

// A placeholder such as "unknown" is not in the note either, so it goes like any unstated value.
function statedText(value: string | undefined, source: string): string | undefined {
  return value !== undefined && states(value, source) ? value : undefined;
}

// When a CV entry ran, as far as the note says. genesis needs a start and reads a missing end as "current".
function statedPeriod(
  dates: { startYear?: number; startMonth?: number; endYear?: number; endMonth?: number },
  source: string,
): { start?: YearMonth; end?: YearMonth; problem?: "noStart" | "noEnd" } {
  const start = statedYearMonth(dates.startYear, dates.startMonth, source);
  const end = statedYearMonth(dates.endYear, dates.endMonth, source);
  if (!start) return { start, end, problem: "noStart" };
  // An end the notes do not give is not "current" just because it is missing.
  if (!end && (dates.endYear !== undefined || !CURRENT.test(normalize(source)))) return { start, end, problem: "noEnd" };
  return { start, end };
}

function parseExperience(entry: unknown, source: string): StudioExperienceProposal[] {
  const parsed = experienceSchema.safeParse(entry);
  if (!parsed.success) return [];
  const { jobTitle, employer, location, ...dates } = parsed.data;
  const { problem, ...when } = statedPeriod(dates, source);
  const experience: StudioExperienceProposal = {
    jobTitle: statedText(jobTitle, source),
    employer: statedText(employer, source),
    location: statedText(location, source),
    ...when,
  };
  // Without a stated title a position is only worth a hint when the note places it: employer and start.
  if (!experience.jobTitle && (!experience.employer || !experience.start)) return [];
  const why = experience.jobTitle ? problem : "noTitle";
  return [why ? { ...experience, problem: why } : experience];
}

function parseEducation(entry: unknown, source: string): StudioEducationProposal[] {
  const parsed = educationSchema.safeParse(entry);
  if (!parsed.success) return [];
  const { field, degree, institution, city, ...dates } = parsed.data;
  const { problem, ...when } = statedPeriod(dates, source);
  const education: StudioEducationProposal = {
    field: statedText(field, source),
    degree: statedText(degree, source),
    institution: statedText(institution, source),
    city: statedText(city, source),
    ...when,
  };
  // An entry naming nothing is no education at all.
  if (!education.field && !education.degree && !education.institution) return [];
  // The start is optional; an unknown start is no problem. Only a known start without a stated end would read as
  // "current", so that one still has to come from the note.
  return [problem === "noEnd" && education.start ? { ...education, problem } : education];
}

// An entry the browser did not take, named on the card so that nothing the assistant proposed vanishes silently.
export interface StudioRejection {
  label: string;
  reason: "nameNotStated" | "notesUnknown";
}

export interface StudioProposal {
  persons: StudioPersonProposal[];
  companies: StudioCompanyProposal[];
  rejected: StudioRejection[];
  // The notebook and the cited passages as they read when the proposal came, for the provenance note. The ids
  // are the entries' `sourceNoteIds`; held here because the notebook's own ids move on (an answer's id counts
  // from the newest). Absent on a card kept before 2026-10-02.
  notebookName?: string;
  sources?: { id: string; text: string }[];
}

// What a provenance note quotes of one passage, and of how many.
const SOURCE_TEXT_MAX = 1200;
const SOURCES_MAX = 12;

/**
 * The model's ProposeStudioRecords arguments are untrusted: every entry is validated on its own and one
 * that fails is dropped rather than failing the whole proposal, and named in `rejected` when it has a name. An entry has to cite notes of the open
 * notebook, and its name has to appear in them; every other value it carries is kept only if a cited note
 * states it, and not only as a guess with a question mark, so a detail the model carried over from a
 * neighbouring note or knew from elsewhere never reaches the card as a fact. What was left out is recorded, so the card can say so. Null when nothing
 * usable is left.
 */
export function parseStudioProposal(args: unknown, notebook: StudioNotebookContext): StudioProposal | null {
  const raw = z
    .object({ persons: z.array(z.unknown()).nullish(), companies: z.array(z.unknown()).nullish() })
    .safeParse(args);
  if (!raw.success) return null;
  const notes = new Map(notebook.notes.map((note) => [note.id, note.text]));
  // The notebook's title heads the whole text, so it counts for every passage: a notebook called
  // "John Ternus" is about him even where a passage says only "he". A cited id the notebook does not hold is
  // left out (seen live 2026-10-02: "a7" next to a real "a1" cost a whole company); every value still has to
  // be stated in the passages that are there, and an entry needs at least one of them.
  const sourceOf = (ids: string[]) => {
    const known = ids.filter((id) => notes.has(id));
    return known.length ? [notebook.name, ...known.map((id) => notes.get(id) ?? "")].join("\n") : null;
  };
  // An expertise the cited sources do not state is left out like any other value; one that only repeats what
  // the entry already says (a job title, the company's name) is none.
  const statedExpertises = (entries: unknown[] | null | undefined, source: string, already: Set<string>) => {
    const kept: StudioExpertiseProposal[] = [];
    const dropped: DroppedField[] = [];
    for (const entry of entries ?? []) {
      const description = expertiseSchema.safeParse(entry).data?.description;
      if (!description || already.has(normalize(description))) continue;
      already.add(normalize(description));
      if (states(description, source)) kept.push({ description });
      else dropped.push({ field: "expertise", value: description });
    }
    return { kept, dropped };
  };
  const rejected: StudioRejection[] = [];
  const persons = (raw.data.persons ?? []).flatMap((entry): StudioPersonProposal[] => {
    const parsed = personSchema.safeParse(entry);
    if (!parsed.success) return [];
    const source = sourceOf(parsed.data.sourceNoteIds);
    const { givenName, familyName } = parsed.data;
    if (!(givenName || familyName)) return [];
    const label = [givenName, familyName].filter(Boolean).join(" ");
    if (source === null) {
      rejected.push({ label, reason: "notesUnknown" });
      return [];
    }
    if ((givenName && !mentions(givenName, source)) || (familyName && !mentions(familyName, source))) {
      rejected.push({ label, reason: "nameNotStated" });
      return [];
    }
    const { experiences, educations, expertises, ...fields } = parsed.data;
    const person: StudioPersonProposal = {
      ...fields,
      experiences: (experiences ?? []).flatMap((entry) => parseExperience(entry, source)),
      educations: (educations ?? []).flatMap((entry) => parseEducation(entry, source)),
      expertises: [],
      dropped: [],
    };
    // A job title is no expertise: the model offered "theoretischer Physiker" as both (seen live, 2026-09-28),
    // and the title row already has it.
    const titles = new Set(
      [fields.jobTitle, ...person.experiences.map((experience) => experience.jobTitle)]
        .filter((title): title is string => Boolean(title))
        .map(normalize),
    );
    const stated = statedExpertises(expertises, source, titles);
    person.expertises = stated.kept;
    person.dropped.push(...stated.dropped);
    for (const field of PERSON_FIELDS) {
      const value = person[field];
      if (value !== undefined && !states(value, source)) {
        person.dropped.push({ field, value });
        person[field] = undefined;
      }
    }
    return [person];
  });
  const companies = (raw.data.companies ?? []).flatMap((entry): StudioCompanyProposal[] => {
    const parsed = companySchema.safeParse(entry);
    if (!parsed.success) return [];
    const source = sourceOf(parsed.data.sourceNoteIds);
    if (source === null || !mentions(parsed.data.name, source)) {
      rejected.push({ label: parsed.data.name, reason: source === null ? "notesUnknown" : "nameNotStated" });
      return [];
    }
    const { expertises, ...fields } = parsed.data;
    const stated = statedExpertises(expertises, source, new Set([normalize(fields.name)]));
    const company: StudioCompanyProposal = { ...fields, expertises: stated.kept, dropped: [...stated.dropped] };
    if (company.summary !== undefined && !states(company.summary, source)) {
      company.dropped.push({ field: "summary", value: company.summary });
      company.summary = undefined;
    }
    if (company.employeeCount !== undefined && !statesCount(company.employeeCount, source)) {
      company.dropped.push({ field: "employeeCount", value: String(company.employeeCount) });
      company.employeeCount = undefined;
    }
    return [company];
  });
  // A company the notes only name as someone's employer is part of that person, not a record of its own: it
  // stays in their employer and positions. Only a company the notes say something about is proposed.
  const employers = new Set(
    persons.flatMap((person) => [person.currentEmployer, ...person.experiences.map((experience) => experience.employer)])
      .filter((name): name is string => Boolean(name))
      .map(normalize),
  );
  const described = companies.filter(
    (company) =>
      company.summary !== undefined ||
      company.employeeCount !== undefined ||
      Boolean(company.expertises?.length) ||
      !employers.has(normalize(company.name)),
  );
  if (!(persons.length || described.length || rejected.length)) return null;
  const cited = [...new Set([...persons, ...described].flatMap((entry) => entry.sourceNoteIds))]
    .filter((id) => notes.has(id))
    .slice(0, SOURCES_MAX)
    .map((id) => {
      const passage = notes.get(id)!;
      return { id, text: passage.length > SOURCE_TEXT_MAX ? `${passage.slice(0, SOURCE_TEXT_MAX)}…` : passage };
    });
  return { persons, companies: described, rejected, notebookName: notebook.name, sources: cited };
}

export function personDisplayName(person: StudioPersonProposal): string {
  return [person.givenName, person.familyName].filter(Boolean).join(" ");
}

/**
 * The login address of a person the Studio creates. genesis still requires `emailPrimary`, which means the login
 * address, and a researched person has none: a reserved, never-deliverable `.invalid` address in the form genesis
 * mints for imports without one (GENESIS-0044: `register-<creator>-<random>@placeholder.invalid`), so every
 * placeholder reads alike. The only place spark makes one up; it goes once genesis accepts a person without it.
 * A found address is the person's contact `email`, never this one.
 */
export function placeholderEmailPrimary(creatorId: string | undefined): string {
  const creator = (creatorId ?? "").replace(/-/g, "").slice(0, 12) || "studio";
  return `register-${creator}-${crypto.randomUUID().replace(/-/g, "")}@placeholder.invalid`;
}

// One input for the card's dry run and for creating the person, so the check and the write send the same data.
export function studioPersonInput(person: StudioPersonProposal, personId: string, dryRun: boolean, emailPrimary: string) {
  return {
    person: personId,
    dryRun,
    data: {
      name: {
        // USUAL, as the profile page writes it: a researched name is the everyday one, not an official record.
        use: NameUse.Usual,
        given: person.givenName ? person.givenName.split(/\s+/) : [],
        family: person.familyName,
      },
      emailPrimary,
      email: person.email,
      jobTitle: person.jobTitle,
      currentEmployer: person.currentEmployer,
      city: person.city,
      website: person.website,
      phone: person.phone,
    },
  };
}
